import { useState, useMemo, FunctionComponent, useEffect, useRef } from "react";
import alerticonError from "../../assets/dropdown/alert-icon-error.svg";
import otlchevronforward1 from "../../assets/dropdown/otlchevronforward1@2x.png";

interface MemberOption {
    key: string;
    name: string;
    isAvailable?: boolean;
    availableHours?: number;
    totalHours?: number;
    utilizationPercentage?: number;
}

interface MemberAvailabilityDropdownProps {
    label: string;
    placeHolder?: string;
    options: MemberOption[];
    onSelect?: (key: string, name: string, type?: any) => void;
    errorMessage?: any;
    initialValue?: any;
    disableDropdown?: boolean;
    clearSelected?: boolean;
    inputType?: 'radio' | 'checkbox';
    checkedItems?: any[];
}

const MemberAvailabilityDropdown: FunctionComponent<MemberAvailabilityDropdownProps> = ({
    label,
    placeHolder = "Select Member",
    options,
    onSelect,
    errorMessage,
    initialValue,
    disableDropdown,
    clearSelected,
    inputType = 'radio',
    checkedItems = []
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState(initialValue || placeHolder);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [inputValue, setInputValue] = useState<string>("");

    useEffect(() => {
        if (clearSelected) {
            setSelectedOption(placeHolder);
        }
    }, [clearSelected, placeHolder]);

    useEffect(() => {
        if (initialValue !== undefined && initialValue !== "" && initialValue !== null) {
            setSelectedOption(initialValue);
        }
    }, [initialValue]);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    function handleDropdownToggle() {
        if (!disableDropdown) {
            setIsOpen(!isOpen);
            if (!isOpen) {
                setInputValue("");
            }
        }
    }

    function handleOptionSelect(key: string, name: string, type?: any) {
        if (inputType === 'radio') {
            setSelectedOption(name);
            setIsOpen(false);
            setInputValue(name);
        }
        
        if (onSelect) {
            onSelect(key, name, type);
        }
    }

    // Filter and sort options: available first, then non-available
    const filteredAndSortedOptions = useMemo(() => {
        let filtered = options.filter((option: MemberOption) =>
            option.name.toLowerCase().includes(inputValue.toLowerCase())
        );

        // Sort: available members first, then non-available
        return filtered.sort((a, b) => {
            if (a.isAvailable && !b.isAvailable) return -1;
            if (!a.isAvailable && b.isAvailable) return 1;
            return 0;
        });
    }, [options, inputValue]);

    // Separate available and non-available members
    const availableMembers = filteredAndSortedOptions.filter(member => member.isAvailable);
    const nonAvailableMembers = filteredAndSortedOptions.filter(member => !member.isAvailable);

    const borderClass = errorMessage
        ? "border-2 border-rose-500 dark:border-rose-500 darkinputborder"
        : "border-surface-border";

    const renderMemberOption = (option: MemberOption, index: number) => {
        const isChecked = inputType === 'checkbox' && checkedItems.some((item: any) => item.key === option.key);
        
        return (
            <div
                key={`${option.key}-${index}`}
                className={`py-3 px-4 hover:bg-gray-100 dark:hover:bg-slate-600 cursor-pointer border-b border-gray-100 dark:border-gray-600 last:border-b-0 memberavailability-input ${
                    !option.isAvailable ? 'opacity-75' : ''
                }`}
                onClick={() => handleOptionSelect(option.key, option.name, option)}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        {inputType === 'checkbox' && (
                            <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => {}} // Handled by parent click
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                        )}
                        <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${
                                option.isAvailable ? 'bg-green-500' : 'bg-red-500'
                            }`}></div>
                            <span className="font-medium text-gray-900 dark:text-white">
                                {option.name}
                            </span>
                        </div>
                    </div>
                    
                    <div className="text-right flex gap-[4px]">
                        {/* <div className={`text-xs font-semibold ${
                            option.isAvailable ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                        }`}>
                            {option.isAvailable ? 'Available' : 'Not Available'}
                        </div> ||  */}
                        {option.availableHours !== undefined && option.totalHours !== undefined && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                {option.availableHours.toFixed(1)}h / {option.totalHours.toFixed(1)}h available ||
                            </div>
                        )}
                        {option.utilizationPercentage !== undefined && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                {option.utilizationPercentage.toFixed(0)}% utilized
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div
            className="relative flex-1 inputnew-div cts-flex1-remd flex flex-col items-start justify-start text-left text-sm mq825:max-w-[100%] text-text-text-primary font-body-reg min-w-full"
            ref={dropdownRef}
        >
            <label className="self-stretch relative font-semibold pb-2 dark:text-white">
                {label}
            </label>
            <div className="self-stretch flex flex-col items-start justify-start text-text-text-secondary dark:text-white">
                <div
                    className={`self-stretch shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] dark:bg-slate-700 rounded-radius-md bg-text-text-tertiary overflow-hidden h-[20px] flex flex-row items-center justify-start py-2.5 px-[15px] gap-[8px] border-[1px] border-solid ${borderClass} mq450:flex-wrap cursor-pointer ${
                        disableDropdown ? "caretask_dropdowndisbale" : ""
                    }`}
                    onClick={handleDropdownToggle}
                >
                    <input
                        type="text"
                        className="flex-1 bg-transparent border-none outline-none text-gray-500 dark:text-white cursor-pointer"
                        placeholder={selectedOption}
                        value={inputValue}
                        onChange={(e) => {
                            setInputValue(e.target.value);
                            setIsOpen(true);
                        }}
                        onClick={() => setIsOpen(true)}
                        disabled={disableDropdown}
                    />
                    <img
                        className={`h-4 w-4 relative overflow-hidden shrink-0 object-contain transform dark-white-img transition-transform ${
                            isOpen ? "rotate-180" : ""
                        }`}
                        alt=""
                        src={otlchevronforward1}
                    />
                </div>
                
                {isOpen && (
                    <div className="cts-zindex-dv absolute left-0 mt-11 w-full self-stretch shadow-[0px_4px_6px_rgba(0,_0,_0,_0.1)] rounded-radius-md bg-white dark:bg-slate-700 border-[1px] border-solid border-surface-border dark:border-slate-600 z-50 max-h-80 overflow-y-auto">
                        {filteredAndSortedOptions.length > 0 ? (
                            <>
                                {/* Available Members Section */}
                                {availableMembers.length > 0 && (
                                    <>
                                        <div className="px-4 py-2 bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-800">
                                            <span className="text-xs font-semibold text-green-700 dark:text-green-300 uppercase tracking-wide flex justify-between">
                                              <span>  Available Members</span> <span>({availableMembers.length})</span>
                                            </span>
                                        </div>
                                        {availableMembers.map((option, index) => renderMemberOption(option, index))}
                                    </>
                                )}
                                
                                {/* Non-Available Members Section */}
                                {nonAvailableMembers.length > 0 && (
                                    <>
                                        <div className="px-4 py-2 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
                                            <span className="text-xs font-semibold text-red-700 dark:text-red-300 uppercase tracking-wide">
                                                Not Available ({nonAvailableMembers.length})
                                            </span>
                                        </div>
                                        {nonAvailableMembers.map((option, index) => renderMemberOption(option, index))}
                                    </>
                                )}
                            </>
                        ) : (
                            <div className="py-4 px-4 text-center text-gray-500 dark:text-gray-400">
                                No members found
                            </div>
                        )}
                    </div>
                )}
            </div>
            
            {errorMessage && (
                <p className="text-red p-2 my-0 cts-bg-error flex items-center gap-[4px] ctcerror-width-blg mt-2">
                    <img src={alerticonError} alt="" /> {errorMessage.message}
                </p>
            )}
        </div>
    );
};

export default MemberAvailabilityDropdown;